import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaFilter, FaSort, FaPlus, FaInfoCircle } from 'react-icons/fa';
import {
  getAllUsersByRoleAsync,
  sendConnectionAsync
} from '../features/connections/connectionSlice';
import {
  viewProfileAsync,
  selectUserProfile,
  selectProfileLoading,
  selectProfileError
} from '../features/wholesaler/wholesalerSlice';
import { productsPerPageWebsite, productsPerPageMobile } from '../app/constants';
import Pagination from '../features/components/Pagination';
import WholesalerNavbar from '../features/wholesaler/components/WholesalerNavbar';
import Footer from '../features/components/Footer';
import { toast } from 'react-toastify';

const WholesalerAddConnection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [sentConnections, setSentConnections] = useState(new Set());

  const { allUsers, loading, error } = useSelector((state) => state.connections);
  const userProfile = useSelector(selectUserProfile);
  const profileLoading = useSelector(selectProfileLoading);
  const profileError = useSelector(selectProfileError);

  useEffect(() => {
    dispatch(getAllUsersByRoleAsync());
  }, [dispatch]);

  const handleViewProfile = async (username) => {
    try {
      setSelectedUser(username);
      setIsProfileOpen(true);
      await dispatch(viewProfileAsync(username)).unwrap();
    } catch (error) {
      toast.error('Failed to load profile');
      setIsProfileOpen(false);
    }
  };

  const handleSendConnection = async (username) => {
    try {
      await dispatch(sendConnectionAsync(username)).unwrap();
      setSentConnections(prev => new Set([...prev, username]));
      toast.success('Connection request sent successfully!');
    } catch (err) {
      toast.error('Failed to send connection request. Please try again.');
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  // Filter users based on search term and city
  const filteredUsers = allUsers.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.firmName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCity = cityFilter === '' || 
      user.city?.toLowerCase().includes(cityFilter.toLowerCase());
    const notSent = !sentConnections.has(user.username);
    return matchesSearch && matchesCity && notSent;
  });

  // Sort filtered users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const dateA = new Date(a.createdAt || 0);
    const dateB = new Date(b.createdAt || 0);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const totalPages = Math.ceil(sortedUsers.length / productsPerPageWebsite);
  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * productsPerPageWebsite,
    currentPage * productsPerPageWebsite
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, cityFilter]);

  const handleCloseProfile = () => {
    setIsProfileOpen(false);
    setSelectedUser(null);
  };

  return (
    <div className="min-h-screen">
      <WholesalerNavbar />
      <div className="bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 pt-20 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <button
            onClick={() => navigate('/wholesaler/connections/requests')}
            className="w-full sm:w-auto px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            Back to Connections
          </button>
        </div>

        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2 className="text-xl font-semibold text-gray-900">Available Retailers</h2>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <div className="relative w-full sm:w-48">
                <input
                  type="text"
                  placeholder="Search by shop name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>
              <div className="relative w-full sm:w-48">
                <input
                  type="text"
                  placeholder="Filter by city"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                />
                <FaFilter className="absolute left-3 top-3 text-gray-400" />
              </div>
              <button
                onClick={toggleSortOrder}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaSort className={`h-4 w-4 transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 p-4 rounded-md mb-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shop Name
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Owner
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.map((user) => (
                  <tr key={user.username}>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.firmName}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.ownerName}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.city}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleViewProfile(user.username)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <FaInfoCircle className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleSendConnection(user.username)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                        >
                          <FaPlus className="mr-1" />
                          Send Request
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && !error && sortedUsers.length > 0 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={productsPerPageWebsite}
              />
            </div>
          )}

          {!loading && !error && sortedUsers.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              No retailers found matching your search criteria
            </div>
          )}
        </div>
      </div>

      {/* Profile Modal */}
      {isProfileOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      User Profile
                    </h3>
                    <div className="mt-4">
                      {profileLoading ? (
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                        </div>
                      ) : profileError ? (
                        <div className="text-red-600">{profileError}</div>
                      ) : userProfile ? (
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Shop Name</h3>
                            <p className="mt-1 text-sm text-gray-900">{userProfile.registeredFirmName}</p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Owner Name</h3>
                            <p className="mt-1 text-sm text-gray-900">{userProfile.ownerName}</p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">City</h3>
                            <p className="mt-1 text-sm text-gray-900">{userProfile.city}</p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Address</h3>
                            <p className="mt-1 text-sm text-gray-900">{userProfile.address}</p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Email</h3>
                            <p className="mt-1 text-sm text-gray-900">{userProfile.email}</p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-500">Phone</h3>
                            <p className="mt-1 text-sm text-gray-900">{userProfile.phoneNumber}</p>
                          </div>
                          {userProfile.gstNumber && (
                            <div>
                              <h3 className="text-sm font-medium text-gray-500">GST Number</h3>
                              <p className="mt-1 text-sm text-gray-900">{userProfile.gstNumber}</p>
                            </div>
                          )}
                          {userProfile.registrationNumber && (
                            <div>
                              <h3 className="text-sm font-medium text-gray-500">Registration Number</h3>
                              <p className="mt-1 text-sm text-gray-900">{userProfile.registrationNumber}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-gray-500">No profile data available</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCloseProfile}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-500 text-base font-medium text-white hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default WholesalerAddConnection;